import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  LEGAL_ACCEPTED_PRIVACY_KEY,
  LEGAL_ACCEPTED_TERMS_KEY,
  LEGAL_FETCH_TIMEOUT_MS,
  LEGAL_LEGACY_ACCEPTED_KEY,
  LEGAL_VERSION_URL,
} from '../constants';

export type LegalManifest = {
  termsVersion: string;
  termsUrl: string;
  termsRequiresReacceptance: boolean;
  privacyVersion: string;
  privacyUrl: string;
  privacyRequiresReacceptance: boolean;
};

export type PendingAcceptance = {
  manifest: LegalManifest;
  termsNeedsReaccept: boolean;
  privacyNeedsReaccept: boolean;
};

/** Fetches and validates the remote legal manifest.
 *  Returns null on any failure (network, timeout, non-2xx, malformed JSON,
 *  schema mismatch) so callers can silently fall back to stored state. */
export async function fetchLegalManifest(): Promise<LegalManifest | null> {
  try {
    const { data } = await axios.get(LEGAL_VERSION_URL, {
      timeout: LEGAL_FETCH_TIMEOUT_MS,
      responseType: 'json',
    });
    if (!data || data.schema !== 1) return null;
    const t = data.terms;
    const p = data.privacy;
    if (!t?.version || !t?.url || !p?.version || !p?.url) return null;
    return {
      termsVersion: String(t.version),
      termsUrl: String(t.url),
      termsRequiresReacceptance: t.requires_reacceptance !== false,
      privacyVersion: String(p.version),
      privacyUrl: String(p.url),
      privacyRequiresReacceptance: p.requires_reacceptance !== false,
    };
  } catch {
    return null;
  }
}

/** Reads the two split acceptance keys, migrating the legacy single-key value if present. */
export async function readAcceptedVersions(): Promise<[string | null, string | null]> {
  try {
    const [terms, privacy] = await Promise.all([
      AsyncStorage.getItem(LEGAL_ACCEPTED_TERMS_KEY),
      AsyncStorage.getItem(LEGAL_ACCEPTED_PRIVACY_KEY),
    ]);
    if (terms !== null || privacy !== null) return [terms, privacy];

    const legacy = await AsyncStorage.getItem(LEGAL_LEGACY_ACCEPTED_KEY);
    if (legacy !== null) {
      await Promise.all([
        AsyncStorage.setItem(LEGAL_ACCEPTED_TERMS_KEY, legacy),
        AsyncStorage.setItem(LEGAL_ACCEPTED_PRIVACY_KEY, legacy),
        AsyncStorage.removeItem(LEGAL_LEGACY_ACCEPTED_KEY),
      ]);
      return [legacy, legacy];
    }
    return [null, null];
  } catch {
    return [null, null];
  }
}

/** Pure decision: given stored versions and the manifest, should we prompt the user?
 *  Returns null when no prompt is needed. */
export function decidePending(
  storedTerms: string | null,
  storedPrivacy: string | null,
  manifest: LegalManifest,
): PendingAcceptance | null {
  const termsNeedsReaccept =
    manifest.termsRequiresReacceptance && storedTerms !== manifest.termsVersion;
  const privacyNeedsReaccept =
    manifest.privacyRequiresReacceptance && storedPrivacy !== manifest.privacyVersion;
  if (!termsNeedsReaccept && !privacyNeedsReaccept) return null;
  return { manifest, termsNeedsReaccept, privacyNeedsReaccept };
}

/** Advance stored versions silently when they drifted but reacceptance isn't required
 *  (e.g. `requires_reacceptance: false` typo-fix bump). Keeps baseline in sync so a
 *  later requires_reacceptance:true bump compares correctly. */
export async function syncSilentlyAcceptedVersions(
  storedTerms: string | null,
  storedPrivacy: string | null,
  manifest: LegalManifest,
): Promise<void> {
  const writes: Promise<void>[] = [];
  if (storedTerms !== manifest.termsVersion) {
    writes.push(AsyncStorage.setItem(LEGAL_ACCEPTED_TERMS_KEY, manifest.termsVersion));
  }
  if (storedPrivacy !== manifest.privacyVersion) {
    writes.push(AsyncStorage.setItem(LEGAL_ACCEPTED_PRIVACY_KEY, manifest.privacyVersion));
  }
  if (writes.length) await Promise.all(writes);
}

export async function recordAcceptance(pending: PendingAcceptance): Promise<void> {
  const writes: Promise<void>[] = [];
  if (pending.termsNeedsReaccept) {
    writes.push(AsyncStorage.setItem(LEGAL_ACCEPTED_TERMS_KEY, pending.manifest.termsVersion));
  }
  if (pending.privacyNeedsReaccept) {
    writes.push(AsyncStorage.setItem(LEGAL_ACCEPTED_PRIVACY_KEY, pending.manifest.privacyVersion));
  }
  if (writes.length) await Promise.all(writes);
}

export function titleFor({ termsNeedsReaccept, privacyNeedsReaccept }: PendingAcceptance): string {
  if (termsNeedsReaccept && privacyNeedsReaccept) return 'Updated Terms & Privacy Policy';
  if (termsNeedsReaccept) return 'Updated Terms of Service';
  return 'Updated Privacy Policy';
}

export function describeUpdate({
  termsNeedsReaccept,
  privacyNeedsReaccept,
}: PendingAcceptance): string {
  if (termsNeedsReaccept && privacyNeedsReaccept) {
    return 'We have updated our Terms of Service and Privacy Policy';
  }
  if (termsNeedsReaccept) return 'We have updated our Terms of Service';
  return 'We have updated our Privacy Policy';
}
